// ============================================================
// THE COMPANY — Custom React Flow Node for a Company Role
// ============================================================

import { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import StatusBadge from "../shared/StatusBadge";
import { STATUS_COLORS } from "../../lib/constants";
import { timeAgo } from "../../lib/timeAgo";
import type { RoleNodeData } from "../../lib/types";

function RoleNode({ data, selected }: NodeProps & { data: RoleNodeData }) {
  const {
    role,
    conversationCount,
    pendingActionItems,
    lastConversationAt,
    dimmed,
  } = data;

  return (
    <div
      className="role-node relative rounded-lg px-3 py-2.5 shadow-sm transition-all duration-150"
      style={{
        width: 200,
        minHeight: 70,
        backgroundColor: "#111118",
        border: `1.5px solid ${selected ? "#6366f1" : "#2a2a3a"}`,
        borderLeft: `3px solid ${STATUS_COLORS[role.status]}`,
        boxShadow: selected
          ? `0 0 12px rgba(99,102,241,0.25)`
          : `inset 3px 0 8px -4px ${STATUS_COLORS[role.status]}33`,
        opacity: dimmed ? 0.25 : 1,
      }}
    >
      {/* Target handle (top) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2 !h-2 !border-none"
        style={{ backgroundColor: "#3a3a4a" }}
      />

      {/* Status dot — top-left */}
      <div className="absolute top-2 left-2.5">
        <StatusBadge status={role.status} size="sm" />
      </div>

      {/* Name + Title */}
      <div className="ml-4 min-w-0">
        <div
          className="text-sm font-semibold truncate leading-tight"
          style={{ color: "#f0f0f5" }}
          title={role.name}
        >
          {role.name}
        </div>
        <div
          className="text-xs truncate leading-tight mt-0.5"
          style={{ color: "#9090a0" }}
          title={role.title}
        >
          {role.title}
        </div>
      </div>

      {/* Last active — bottom-left */}
      {lastConversationAt && (
        <div
          className="absolute bottom-1.5 left-2.5 text-[9px] leading-none"
          style={{ color: "#6b6b7b" }}
        >
          {timeAgo(lastConversationAt)}
        </div>
      )}

      {/* Badges — bottom-right */}
      <div className="absolute bottom-1.5 right-2 flex items-center gap-1">
        {pendingActionItems > 0 && (
          <div
            className="text-[10px] font-medium rounded-full px-1.5 py-0.5 leading-none"
            style={{
              backgroundColor: "rgba(99, 102, 241, 0.12)",
              color: "#818cf8",
              border: "1px solid rgba(99, 102, 241, 0.2)",
            }}
            title={`${pendingActionItems} pending action item${pendingActionItems > 1 ? "s" : ""}`}
          >
            {pendingActionItems}
          </div>
        )}
        {conversationCount > 0 && (
          <div
            className="text-[10px] font-medium rounded-full px-1.5 py-0.5 leading-none"
            style={{
              backgroundColor: "rgba(26, 26, 36, 0.8)",
              color: "#7a7a8a",
              border: "1px solid #2a2a3a",
            }}
          >
            {conversationCount}
          </div>
        )}
      </div>

      {/* Source handle (bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2 !h-2 !border-none"
        style={{ backgroundColor: "#3a3a4a" }}
      />
    </div>
  );
}

export default memo(RoleNode);
